import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from './components/ui/button';
import Hero from './components/Hero';
import Features from './components/Features';
import UniqueFeatures from './components/UniqueFeatures';
import Benefits from './components/Benefits';
import VideoSection from './components/VideoSection';
import PackageContent from './components/PackageContent';
import Accessories from './components/Accessories';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';
import './App.css';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="App min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Zosia mała nauczycielka - Robot Edukacyjny do Nauki Kodowania STEAM | EDUKO</title>
        <meta name="description" content="Zosia mała nauczycielka - jedyny robot mówiący po polsku pełnymi zdaniami! Bezekranowy, bez internetu. Idealny do nauki kodowania STEAM w przedszkolach, szkołach podstawowych i gabinetach logopedycznych. Wspiera podstawę programową." />
        <meta name="keywords" content="Zosia mała nauczycielka, robot edukacyjny, nauka kodowania, STEAM, edukacja przedszkolna, edukacja wczesnoszkolna, robot dla dzieci, kodowanie bez ekranu, robot mówiący po polsku, logopedia, terapia dzieci, EDUKO, bezekranowy robot, nauka programowania" />
        <meta name="author" content="EDUKO" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://sklep.eduko.pl/products/zosia-mala-nauczycielka-interaktywny-robot-edukacyiny" />
        
        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content="Zosia mała nauczycielka - Robot Edukacyjny do Nauki Kodowania STEAM" />
        <meta property="og:description" content="Jedyny robot mówiący po polsku pełnymi zdaniami! Bezekranowy, bez internetu. Idealny dla przedszkoli, szkół i logopedów. Wspiera metodologię STEAM." />
        <meta property="og:image" content="https://sklep.eduko.pl/cdn/shop/files/zosia-product-1.png?v=1694337855&width=1946" />
        <meta property="og:url" content="https://sklep.eduko.pl/products/zosia-mala-nauczycielka-interaktywny-robot-edukacyiny" />
        <meta property="og:type" content="product" />
        <meta property="og:locale" content="pl_PL" />
        <meta property="og:site_name" content="EDUKO - Zosia mała nauczycielka" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Zosia mała nauczycielka - Robot Edukacyjny STEAM" />
        <meta name="twitter:description" content="Jedyny robot mówiący po polsku pełnymi zdaniami! Bezekranowy, bez internetu. Idealny dla przedszkoli, szkół i logopedów." />
        <meta name="twitter:image" content="https://sklep.eduko.pl/cdn/shop/files/zosia-product-1.png?v=1694337855&width=1946" />
        
        {/* Additional SEO tags */}
        <meta name="geo.region" content="PL" />
        <meta name="geo.placename" content="Warszawa" />
        <meta name="language" content="Polish" />
      </Helmet>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center group">
              <span className="text-xl sm:text-2xl font-bold text-orange-500">
                Zosia mała nauczycielka
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('features')}
                className="text-slate-700 hover:text-orange-500 transition-colors"
              >
                Możliwości
              </button>
              <button
                onClick={() => scrollToSection('benefits')}
                className="text-slate-700 hover:text-orange-500 transition-colors"
              >
                Korzyści
              </button>
              <button
                onClick={() => scrollToSection('why-zosia')}
                className="text-slate-700 hover:text-orange-500 transition-colors"
              >
                Dlaczego Zosia?
              </button>
              <button
                onClick={() => scrollToSection('package')}
                className="text-slate-700 hover:text-orange-500 transition-colors"
              >
                Zestaw
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="text-slate-700 hover:text-orange-500 transition-colors"
              >
                Opinie
              </button>
              <button
                onClick={() => scrollToSection('faq')}
                className="text-slate-700 hover:text-orange-500 transition-colors"
              >
                FAQ
              </button>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
              >
                Kontakt
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-700 hover:text-orange-500"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <div className="flex flex-col space-y-4">
                <button
                  onClick={() => scrollToSection('features')}
                  className="text-slate-700 hover:text-orange-500 text-left"
                >
                  Możliwości
                </button>
                <button
                  onClick={() => scrollToSection('benefits')}
                  className="text-slate-700 hover:text-orange-500 text-left"
                >
                  Korzyści
                </button>
                <button
                  onClick={() => scrollToSection('why-zosia')}
                  className="text-slate-700 hover:text-orange-500 text-left"
                >
                  Dlaczego Zosia?
                </button>
                <button
                  onClick={() => scrollToSection('package')}
                  className="text-slate-700 hover:text-orange-500 text-left"
                >
                  Zestaw
                </button>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="text-slate-700 hover:text-orange-500 text-left"
                >
                  Opinie
                </button>
                <button
                  onClick={() => scrollToSection('faq')}
                  className="text-slate-700 hover:text-orange-500 text-left"
                >
                  FAQ
                </button>
                <Button
                  onClick={() => scrollToSection('contact')}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white w-full"
                >
                  Kontakt
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-16">
        <Hero />
        <Features />
        <VideoSection />
        <UniqueFeatures />
        <Benefits />
        <PackageContent />
        <Accessories />
        <Testimonials />
        <FAQ />
        <ContactForm />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default App;
